import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  ExternalLink,
  Plus,
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
  X,
  Save,
  BarChart3,
  MessageCircle,
  Eye,
} from "lucide-react";
import { api, fetchCatalog, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ICON_OPTIONS, getIcon } from "@/data/catalogHelpers";
import ImageUploader from "@/components/admin/ImageUploader";
import ModalityImageEditor from "@/components/admin/ModalityImageEditor";
import ModalityPage from "@/components/catalog/ModalityPage";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  ResponsiveContainer,
  Cell,
  Tooltip,
} from "recharts";

const emptyModality = {
  title: "",
  slug: "",
  phrase: "",
  icon: "Sparkles",
  image: "",
  imageFocusX: 50,
  imageFocusY: 50,
  imageFit: "cover",
  imageZoom: 100,
  imageHeightPct: 0,
  consultOnly: false,
  featured: false,
  compact: false,
  procedures: [],
};

const inputCls =
  "bg-black/40 border-[rgba(212,175,55,0.3)] text-white focus-visible:ring-[var(--gold)]";
const lblCls =
  "font-body text-[0.62rem] text-[var(--nude)] uppercase tracking-widest";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [studio, setStudio] = useState(null);
  const [modalities, setModalities] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [editing, setEditing] = useState(null); // modality being edited (or new)
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const data = await fetchCatalog();
    setStudio(data.studio);
    setModalities(data.modalities || []);
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/analytics");
      setAnalytics(data);
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
    loadAnalytics();
  }, [load, loadAnalytics]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  // ---------- Modality ops ----------
  const saveModality = async (mod) => {
    setSaving(true);
    try {
      if (mod.id) {
        await api.put(`/admin/modalities/${mod.id}`, mod);
        toast.success("Modalidade atualizada");
      } else {
        await api.post("/admin/modalities", mod);
        toast.success("Modalidade criada");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const deleteModality = async (id) => {
    if (!window.confirm("Remover esta modalidade?")) return;
    try {
      await api.delete(`/admin/modalities/${id}`);
      toast.success("Modalidade removida");
      await load();
    } catch (err) {
      toast.error("Erro ao remover");
    }
  };

  const move = async (index, dir) => {
    const arr = [...modalities];
    const target = index + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setModalities(arr);
    try {
      await api.post("/admin/modalities/reorder", arr.map((m) => m.id));
    } catch (err) {
      toast.error("Erro ao reordenar");
      load();
    }
  };

  const saveStudio = async () => {
    setSaving(true);
    try {
      const payload = { ...studio };
      delete payload.key;
      await api.put("/admin/studio", payload);
      toast.success("Marca atualizada");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[var(--deep-black)]">
      {/* Header */}
      <header className="sticky top-0 z-20 flex items-center justify-between px-5 sm:px-8 py-4 border-b border-[rgba(212,175,55,0.18)] bg-black/70 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <span className="font-heading text-gold-metallic text-3xl leading-none">BR</span>
          <div className="hidden sm:block">
            <p className="font-body text-[0.6rem] uppercase tracking-[0.3em] text-[var(--nude)]">
              Painel Administrativo
            </p>
            <p className="font-body text-[0.55rem] text-[var(--muted)]">
              {user?.email}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            data-testid="view-catalog-button"
            onClick={() => window.open("/", "_blank")}
            className="border-[rgba(212,175,55,0.35)] text-[var(--nude)] hover:text-[var(--gold)] bg-transparent text-xs"
          >
            <ExternalLink size={14} className="mr-1.5" /> Ver catálogo
          </Button>
          <Button
            data-testid="logout-button"
            onClick={handleLogout}
            className="bg-[var(--gold)] text-black hover:bg-[var(--champagne)] text-xs"
          >
            <LogOut size={14} className="mr-1.5" /> Sair
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <Tabs defaultValue="modalities">
          <TabsList className="bg-black/40 border border-[rgba(212,175,55,0.2)]">
            <TabsTrigger value="modalities" data-testid="tab-modalities" className="data-[state=active]:bg-[var(--gold)] data-[state=active]:text-black">
              Modalidades
            </TabsTrigger>
            <TabsTrigger value="brand" data-testid="tab-brand" className="data-[state=active]:bg-[var(--gold)] data-[state=active]:text-black">
              Marca & Contato
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" onClick={loadAnalytics} className="data-[state=active]:bg-[var(--gold)] data-[state=active]:text-black">
              Agendamentos
            </TabsTrigger>
          </TabsList>

          {/* Modalities tab */}
          <TabsContent value="modalities" className="mt-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-3xl text-[var(--offwhite)]">
                Modalidades ({modalities.length})
              </h2>
              <Button
                data-testid="new-modality-button"
                onClick={() => setEditing({ ...emptyModality })}
                className="bg-[var(--gold)] text-black hover:bg-[var(--champagne)] text-xs"
              >
                <Plus size={15} className="mr-1.5" /> Nova modalidade
              </Button>
            </div>

            <div className="space-y-2">
              {modalities.map((m, i) => {
                const Icon = getIcon(m.icon);
                return (
                  <div
                    key={m.id}
                    data-testid={`admin-modality-row-${i}`}
                    className="flex items-center gap-3 border border-[rgba(212,175,55,0.15)] px-4 py-3 bg-black/30 hover:border-[rgba(212,175,55,0.4)] transition-colors"
                  >
                    <span className="w-9 h-9 shrink-0 flex items-center justify-center border border-[rgba(212,175,55,0.3)] rounded-full">
                      <Icon size={16} className="text-[var(--gold)]" strokeWidth={1.4} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm text-[var(--offwhite)] truncate">{m.title}</p>
                      <p className="font-body text-[0.62rem] text-[var(--muted)]">
                        {m.consultOnly ? "Valores sob consulta" : `${(m.procedures || []).length} procedimento(s)`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Mover para cima" className="w-8 h-8 flex items-center justify-center text-[var(--muted)] hover:text-[var(--gold)] disabled:opacity-30">
                        <ArrowUp size={15} />
                      </button>
                      <button onClick={() => move(i, 1)} disabled={i === modalities.length - 1} aria-label="Mover para baixo" className="w-8 h-8 flex items-center justify-center text-[var(--muted)] hover:text-[var(--gold)] disabled:opacity-30">
                        <ArrowDown size={15} />
                      </button>
                      <button data-testid={`edit-modality-${i}`} onClick={() => setEditing({ ...m })} aria-label="Editar" className="w-8 h-8 flex items-center justify-center text-[var(--nude)] hover:text-[var(--gold)]">
                        <Pencil size={15} />
                      </button>
                      <button data-testid={`delete-modality-${i}`} onClick={() => deleteModality(m.id)} aria-label="Remover" className="w-8 h-8 flex items-center justify-center text-[var(--muted)] hover:text-red-400">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Brand tab */}
          <TabsContent value="brand" className="mt-6">
            {studio && (
              <StudioForm studio={studio} setStudio={setStudio} onSave={saveStudio} saving={saving} />
            )}
          </TabsContent>

          {/* Analytics tab */}
          <TabsContent value="analytics" className="mt-6">
            <AnalyticsPanel analytics={analytics} onRefresh={loadAnalytics} />
          </TabsContent>
        </Tabs>
      </main>

      {editing && (
        <ModalityDialog
          modality={editing}
          studio={studio}
          totalPages={modalities.length + 3}
          onClose={() => setEditing(null)}
          onSave={saveModality}
          saving={saving}
        />
      )}
    </div>
  );
}

/* ---------------- Studio form ---------------- */
function StudioForm({ studio, setStudio, onSave, saving }) {
  const set = (k, v) => setStudio((s) => ({ ...s, [k]: v }));
  const setDiff = (i, v) => {
    const d = [...(studio.differentials || [])];
    d[i] = v;
    set("differentials", d);
  };

  const fields = [
    ["studioName", "Nome do Studio"],
    ["brandName", "Nome (marca)"],
    ["sloganMain", "Slogan principal"],
    ["sloganSecondary", "Slogan secundário"],
    ["whatsapp", "WhatsApp (só números, ex: 5511995470626)"],
    ["whatsappDisplay", "WhatsApp (exibição)"],
    ["region", "Região de atendimento"],
    ["giftLine", "Frase do presente"],
  ];

  return (
    <div className="max-w-2xl space-y-4" data-testid="studio-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.map(([k, label]) => (
          <div key={k} className={`space-y-1.5 ${["sloganMain", "sloganSecondary", "region"].includes(k) ? "sm:col-span-2" : ""}`}>
            <Label className={lblCls}>{label}</Label>
            <Input
              data-testid={`studio-${k}`}
              value={studio[k] || ""}
              onChange={(e) => set(k, e.target.value)}
              className={inputCls}
            />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
        <ImageUploader testid="studio-logo-uploader" label="Logo (PNG transparente)" value={studio.logoImage} onChange={(v) => set("logoImage", v)} />
        <ImageUploader testid="studio-cover-uploader" label="Imagem da capa" value={studio.coverImage} onChange={(v) => set("coverImage", v)} />
        <ImageUploader testid="studio-contact-uploader" label="Imagem de contato" value={studio.contactImage} onChange={(v) => set("contactImage", v)} />
      </div>

      <div className="space-y-2">
        <Label className={lblCls}>Diferenciais</Label>
        <div className="grid grid-cols-2 gap-3">
          {(studio.differentials || []).map((d, i) => (
            <Input key={i} value={d} onChange={(e) => setDiff(i, e.target.value)} className={inputCls} />
          ))}
        </div>
      </div>

      <Button
        onClick={onSave}
        disabled={saving}
        data-testid="save-studio-button"
        className="bg-[var(--gold)] text-black hover:bg-[var(--champagne)] text-xs"
      >
        <Save size={14} className="mr-1.5" /> {saving ? "Salvando..." : "Salvar marca"}
      </Button>
    </div>
  );
}

/* ---------------- Analytics panel ---------------- */
function AnalyticsPanel({ analytics, onRefresh }) {
  const items = analytics?.items || [];
  const total = analytics?.total || 0;
  const max = items.reduce((mx, i) => Math.max(mx, i.clicks), 0) || 1;

  return (
    <div className="max-w-3xl space-y-5" data-testid="analytics-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 flex items-center justify-center border border-[rgba(212,175,55,0.3)] rounded-full">
            <BarChart3 size={18} className="text-[var(--gold)]" strokeWidth={1.4} />
          </span>
          <div>
            <p className="font-heading text-2xl text-[var(--offwhite)] leading-none">Interesse por procedimento</p>
            <p className="font-body text-[0.62rem] text-[var(--muted)] mt-1">
              Cliques em &quot;Agendar pelo WhatsApp&quot; no catálogo
            </p>
          </div>
        </div>
        <button
          onClick={onRefresh}
          data-testid="refresh-analytics-button"
          className="text-[0.62rem] uppercase tracking-widest font-body text-[var(--nude)] hover:text-[var(--gold)] border border-[rgba(212,175,55,0.3)] px-3 py-2"
        >
          Atualizar
        </button>
      </div>

      <div className="border border-[rgba(212,175,55,0.2)] bg-black/30 p-4">
        <p className="font-body text-[0.6rem] uppercase tracking-[0.3em] text-[var(--muted)]">Total de cliques</p>
        <p className="font-heading text-4xl text-gold-metallic" data-testid="analytics-total">{total}</p>
      </div>

      <TrendChart last7={analytics?.last7 || []} weekday={analytics?.weekday || []} total={total} />

      <div className="space-y-2.5">
        {items.map((it, idx) => (
          <div key={it.id} data-testid={`analytics-row-${idx}`} className="border border-[rgba(212,175,55,0.12)] bg-black/20 px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-body text-sm text-[var(--offwhite)] flex items-center gap-2">
                <span className="font-heading text-[var(--gold)] text-base w-5">{idx + 1}</span>
                {it.title}
              </span>
              <span className="flex items-center gap-1.5 font-body text-sm font-semibold text-[var(--gold)]">
                <MessageCircle size={13} strokeWidth={1.6} /> {it.clicks}
              </span>
            </div>
            <div className="h-[3px] bg-[rgba(212,175,55,0.12)]">
              <div className="h-full bg-[var(--gold)]" style={{ width: `${(it.clicks / max) * 100}%` }} />
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="font-body text-sm text-[var(--muted)] text-center py-8">Ainda não há cliques registrados.</p>
        )}
      </div>
    </div>
  );
}

/* ---------------- Trend chart ---------------- */
function ChartTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-[var(--satin-black)] border border-[rgba(212,175,55,0.4)] px-3 py-2">
      <p className="font-body text-[0.6rem] text-[var(--nude)]">{p.date || p.label}</p>
      <p className="font-body text-sm text-[var(--gold)] font-semibold">{p.count} clique(s)</p>
    </div>
  );
}

function TrendChart({ last7, weekday, total }) {
  const maxDay = last7.reduce((mx, d) => Math.max(mx, d.count), 0);
  // quietest weekday (only meaningful once there is data)
  let quietest = null;
  if (total > 0 && weekday.length) {
    quietest = weekday.reduce((min, w) => (w.count < min.count ? w : min), weekday[0]);
  }

  return (
    <div className="border border-[rgba(212,175,55,0.2)] bg-black/30 p-4" data-testid="trend-chart">
      <p className="font-body text-[0.6rem] uppercase tracking-[0.3em] text-[var(--muted)] mb-3">
        Últimos 7 dias
      </p>
      <div style={{ width: "100%", height: 150 }}>
        <ResponsiveContainer>
          <BarChart data={last7} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
            <XAxis
              dataKey="label"
              tick={{ fill: "#9a8f84", fontSize: 11, fontFamily: "Montserrat" }}
              axisLine={{ stroke: "rgba(212,175,55,0.2)" }}
              tickLine={false}
            />
            <Tooltip cursor={{ fill: "rgba(212,175,55,0.08)" }} content={<ChartTooltip />} />
            <Bar dataKey="count" radius={[3, 3, 0, 0]} maxBarSize={34}>
              {last7.map((d, i) => (
                <Cell
                  key={i}
                  fill={d.count === maxDay && maxDay > 0 ? "#F3E5AB" : "#D4AF37"}
                  fillOpacity={d.count === 0 ? 0.18 : 1}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {quietest && quietest.count === 0 && (
        <p className="font-body text-[0.7rem] text-[var(--nude)] mt-2">
          Dia mais tranquilo: <span className="text-[var(--gold)] font-semibold">{quietest.label}</span> — ótimo para
          divulgar uma promoção.
        </p>
      )}
      {quietest && quietest.count > 0 && (
        <p className="font-body text-[0.7rem] text-[var(--nude)] mt-2">
          Dia de menor procura: <span className="text-[var(--gold)] font-semibold">{quietest.label}</span> ({quietest.count})
          — considere uma promoção nesse dia.
        </p>
      )}
    </div>
  );
}

/* ---------------- Modality dialog ---------------- */
function ModalityDialog({ modality, studio, totalPages, onClose, onSave, saving }) {
  const [m, setM] = useState(modality);
  const [previewOpen, setPreviewOpen] = useState(false);
  const set = (k, v) => setM((prev) => ({ ...prev, [k]: v }));

  const setProc = (i, field, v) => {
    const p = [...(m.procedures || [])];
    p[i] = { ...p[i], [field]: v };
    set("procedures", p);
  };
  const addProc = () => set("procedures", [...(m.procedures || []), { name: "", price: "" }]);
  const removeProc = (i) => set("procedures", m.procedures.filter((_, idx) => idx !== i));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--satin-black)] border border-[rgba(212,175,55,0.3)] text-white max-w-lg max-h-[90vh] overflow-y-auto thin-scroll">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[var(--offwhite)]">
            {m.id ? "Editar modalidade" : "Nova modalidade"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className={lblCls}>Título</Label>
            <Input data-testid="modality-title-input" value={m.title} onChange={(e) => set("title", e.target.value)} className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={lblCls}>Frase de apoio</Label>
            <Textarea value={m.phrase} onChange={(e) => set("phrase", e.target.value)} className={inputCls} rows={2} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className={lblCls}>Ícone</Label>
              <Select value={m.icon} onValueChange={(v) => set("icon", v)}>
                <SelectTrigger className={inputCls} data-testid="modality-icon-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--satin-black)] border-[rgba(212,175,55,0.3)] text-white max-h-60">
                  {ICON_OPTIONS.map((name) => (
                    <SelectItem key={name} value={name} className="text-white focus:bg-[rgba(212,175,55,0.15)]">
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className={lblCls}>Slug (opcional)</Label>
              <Input value={m.slug || ""} onChange={(e) => set("slug", e.target.value)} className={inputCls} />
            </div>
          </div>

          <ModalityImageEditor
            value={m.image}
            onChange={(v) => set("image", v)}
            focusX={m.imageFocusX ?? 50}
            focusY={m.imageFocusY ?? 50}
            fit={m.imageFit || "cover"}
            zoom={m.imageZoom ?? 100}
            onFocusChange={(x, y) => setM((prev) => ({ ...prev, imageFocusX: x, imageFocusY: y }))}
            onFitChange={(f) => set("imageFit", f)}
            onZoomChange={(z) => set("imageZoom", z)}
          />

          {m.image && (
            <div className="space-y-1.5" data-testid="image-height-control">
              <div className="flex items-center justify-between">
                <Label className={lblCls}>Altura da caixa no catálogo</Label>
                <span className="font-body text-[0.62rem] uppercase tracking-widest text-[var(--gold)]">
                  {m.imageHeightPct > 0 ? `${m.imageHeightPct}%` : "Automática"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={70}
                  step={2}
                  value={m.imageHeightPct || 0}
                  onChange={(e) => set("imageHeightPct", Number(e.target.value))}
                  data-testid="image-height-slider"
                  aria-label="Altura da caixa da mídia no catálogo"
                  className="flex-1 h-1 accent-[var(--gold)] cursor-pointer"
                />
                <button
                  type="button"
                  data-testid="reset-height-btn"
                  onClick={() => set("imageHeightPct", 0)}
                  className="px-2 py-1 border border-[rgba(212,175,55,0.3)] text-[var(--nude)] hover:text-[var(--gold)] transition-colors text-[0.58rem] uppercase tracking-widest font-body"
                >
                  Auto
                </button>
              </div>
              <p className="font-body text-[0.6rem] text-[var(--muted)]">
                Vale para imagens e vídeos. Deixe em &quot;Automática&quot; para o padrão premium, ou aumente para dar mais destaque à mídia.
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-6 pt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={m.consultOnly} onCheckedChange={(v) => set("consultOnly", v)} data-testid="modality-consult-switch" />
              <span className={lblCls}>Valores sob consulta</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={m.featured} onCheckedChange={(v) => set("featured", v)} />
              <span className={lblCls}>Destaque</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={m.compact} onCheckedChange={(v) => set("compact", v)} />
              <span className={lblCls}>Compacto</span>
            </label>
          </div>

          {!m.consultOnly && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className={lblCls}>Procedimentos</Label>
                <Button size="sm" variant="outline" onClick={addProc} data-testid="add-procedure-button" className="border-[rgba(212,175,55,0.35)] text-[var(--nude)] bg-transparent h-7 text-xs">
                  <Plus size={13} className="mr-1" /> Adicionar
                </Button>
              </div>
              <div className="space-y-2">
                {(m.procedures || []).map((p, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input placeholder="Nome" value={p.name} onChange={(e) => setProc(i, "name", e.target.value)} className={`${inputCls} flex-1`} data-testid={`proc-name-${i}`} />
                    <Input placeholder="R$ 0,00" value={p.price} onChange={(e) => setProc(i, "price", e.target.value)} className={`${inputCls} w-32`} data-testid={`proc-price-${i}`} />
                    <button onClick={() => removeProc(i)} aria-label="Remover procedimento" className="text-[var(--muted)] hover:text-red-400 shrink-0">
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[rgba(212,175,55,0.3)] text-[var(--nude)] bg-transparent text-xs">
            Cancelar
          </Button>
          <Button
            variant="outline"
            onClick={() => setPreviewOpen(true)}
            disabled={!m.title}
            data-testid="preview-modality-button"
            className="border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-black bg-transparent text-xs"
          >
            <Eye size={14} className="mr-1.5" /> Ver preview
          </Button>
          <Button
            onClick={() => onSave(m)}
            disabled={saving || !m.title}
            data-testid="save-modality-button"
            className="bg-[var(--gold)] text-black hover:bg-[var(--champagne)] text-xs"
          >
            <Save size={14} className="mr-1.5" /> {saving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
      {previewOpen && (
        <ModalityPreviewDialog
          modality={m}
          studio={studio}
          totalPages={totalPages}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </Dialog>
  );
}

/* ---------------- Modality preview dialog ---------------- */
function ModalityPreviewDialog({ modality, studio, totalPages, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="modality-preview-dialog"
        className="bg-[var(--satin-black)] border border-[rgba(212,175,55,0.3)] text-white max-w-md p-0 overflow-hidden"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[rgba(212,175,55,0.15)]">
          <DialogTitle className="font-heading text-xl text-[var(--offwhite)] flex items-center gap-2">
            <Eye size={16} className="text-[var(--gold)]" /> Preview no catálogo
          </DialogTitle>
          <p className="font-body text-[0.62rem] text-[var(--muted)]">
            Exatamente como aparecerá para o cliente antes de publicar.
          </p>
        </DialogHeader>
        <div className="p-4 bg-[var(--deep-black)] flex items-center justify-center">
          <div
            className="w-full max-w-[380px] shadow-[0_20px_60px_rgba(0,0,0,0.6)] border border-[rgba(212,175,55,0.15)]"
            style={{ aspectRatio: "9 / 16" }}
          >
            <ModalityPage
              studio={studio || {}}
              modality={modality}
              pageNumber={1}
              totalPages={totalPages || 1}
            />
          </div>
        </div>
        <DialogFooter className="px-5 py-3 border-t border-[rgba(212,175,55,0.15)]">
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="close-preview-button"
            className="border-[rgba(212,175,55,0.3)] text-[var(--nude)] bg-transparent text-xs"
          >
            Voltar a editar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
